import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { mergeRefs } from '../../BKJSelect/utils/mergeRefs.js';
import { BKJTextInput } from '../../BKJTextInput/index.js';
import { useRightIconProps } from '../BKJComboBoxAsync.utils.js';
import { useBKJComboBoxAsyncContext } from './BKJComboBoxAsync.context.js';
import { getComboBoxFocusBorderStyles } from '../../BKJComboBox/BKJComboBox.styles.js';
const variantMap = {
    Search: 'ComboBoxSeamlessRounded',
};
export const BKJComboBoxAsyncInput = forwardRef(({ autoComplete, className, overrides = [], showFullFocusBorder = true, ...rest }, ref) => {
    const { label, variant = 'Search', action, error, required, getComboboxProps, getMenuProps, setAnchorElement, getInputProps, searchValue, getLabelProps, isOpen, onInputFocus, onInputKeyDown, placeholder, selectedItem, searchKey, leftIconProps, disabled, onSearchChange, isDropdownReverse, } = useBKJComboBoxAsyncContext();
    const rightIconProps = useRightIconProps(action, disabled);
    const isDropdownDisplayed = overrides.includes('NoInitialResults')
        ? typeof searchValue === 'string' && searchValue === ''
            ? false
            : isOpen
        : isOpen;
    // WEB-4815 / T029 / FR-001 (inherited from WEB-4811 FR-001): read the
    // listbox id from getMenuProps so aria-controls matches the id the
    // listbox actually renders with. Downshift shares the same id counter
    // across getInputProps / getMenuProps — we read it explicitly for clarity.
    const menuId = getMenuProps({}, { suppressRefError: true }).id;
    const inputProps = getInputProps({
        ...rest,
        onKeyDown: onInputKeyDown,
        value: !isOpen ? selectedItem?.label || '' : searchValue,
        onFocus: onInputFocus,
        ref,
        name: searchKey,
        onChange: onSearchChange,
        required,
        label,
        disabled,
        placeholder,
        // WEB-4815 / T029 / FR-001: explicit ARIA on the input. Downshift
        // 6.1.12 emits `role="combobox"` + `aria-haspopup` via getComboboxProps
        // on the fieldset wrapper (ARIA 1.1). We move them to the input for
        // the ARIA 1.2 editable-combobox pattern that WEB-4811 adopted.
        role: 'combobox',
        'aria-haspopup': 'listbox',
        'aria-expanded': isOpen,
        'aria-controls': menuId,
        'aria-autocomplete': 'list',
    });
    // WEB-4815 / T030 / R2 / I-01 (inherited): getComboboxProps() is still
    // called — its ref registers the fieldset with Downshift's
    // mouse-and-touch tracker, which drives the "click outside closes"
    // contract. We null out the ARIA attributes it would emit because
    // FR-001 puts role=combobox / aria-haspopup / aria-expanded on the
    // input. Net effect: ref stays, ARIA doesn't duplicate.
    const fieldSetRefProps = getComboboxProps({
        role: undefined,
        'aria-haspopup': undefined,
        'aria-owns': undefined,
        'aria-expanded': undefined,
    });
    const focusBorderStyles = getComboBoxFocusBorderStyles({
        showFullFocusBorder,
        isDropdownReverse,
    });
    if (overrides.includes('NoInitialResults')) {
        return (_jsx(BKJTextInput, { isOpen: isDropdownDisplayed, labelProps: getLabelProps(), fieldSetProps: fieldSetRefProps, variant: variantMap[variant], ...inputProps, className: className, css: isDropdownDisplayed && focusBorderStyles, rightIconProps: rightIconProps, leftIconProps: leftIconProps, error: isDropdownDisplayed ? false : error, autoComplete: autoComplete, ref: mergeRefs([setAnchorElement, inputProps.ref]), useFocusBorder: !isDropdownDisplayed }));
    }
    return (_jsx(BKJTextInput, { isOpen: isOpen, labelProps: getLabelProps(), fieldSetProps: fieldSetRefProps, variant: variantMap[variant], ...inputProps, className: className, css: isOpen && focusBorderStyles, rightIconProps: rightIconProps, leftIconProps: leftIconProps, error: isOpen ? false : error, autoComplete: autoComplete, ref: mergeRefs([setAnchorElement, inputProps.ref]), useFocusBorder: !isOpen }));
});
//# sourceMappingURL=BKJComboBoxAsyncInput.js.map